import type { Order } from '../types';

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const COMPLETED_HARD_CUTOFF_HOURS = 4;
const LEAD_TIME_MINUTES = 15;

export interface RetentionOptions {
  /**
   * How long a `completed` order stays on the screen after its last
   * `updatedAt`. Sourced from `NEXT_PUBLIC_DONE_RETENTION_MINUTES` /
   * the matching Vite env var.
   */
  doneRetentionMinutes: number;
  /** Override "now" for tests. */
  now?: number;
}

/**
 * §2.5 - completed orders are pruned after the *earlier* of:
 *   - doneRetentionMinutes after `updatedAt`
 *   - COMPLETED_HARD_CUTOFF_HOURS after `pickupAt` (or `createdAt` when pickupAt missing)
 *
 * Cancelled orders never reach this function; they're dropped upstream.
 */
export function shouldRetain(order: Order, opts: RetentionOptions): boolean {
  if (order.status !== 'completed') return true;
  const now = opts.now ?? Date.now();

  const softCutoff =
    parseIso(order.updatedAt) + opts.doneRetentionMinutes * MS_PER_MINUTE;
  const hardAnchor = parseIso(order.pickupAt) || parseIso(order.createdAt);
  const hardCutoff = hardAnchor + COMPLETED_HARD_CUTOFF_HOURS * MS_PER_HOUR;

  // If neither anchor parsed, fall back to keeping the order - missing
  // timestamps shouldn't cause us to silently disappear cards.
  if (softCutoff <= 0 && hardCutoff <= 0) return true;

  const earliestCutoff =
    softCutoff > 0 && hardCutoff > 0
      ? Math.min(softCutoff, hardCutoff)
      : Math.max(softCutoff, hardCutoff);

  return now < earliestCutoff;
}

/**
 * §2.6 - hide pending / received scheduled orders until they're within
 * LEAD_TIME_MINUTES of pickup. Once an order moves past `received` the
 * gate no longer applies.
 */
export function isWithinLeadTime(order: Order, now: number = Date.now()): boolean {
  if (order.status !== 'pending' && order.status !== 'received') return true;
  if (!order.pickupAt) return true;
  const pickup = parseIso(order.pickupAt);
  if (pickup <= 0) return true;
  return now >= pickup - LEAD_TIME_MINUTES * MS_PER_MINUTE;
}

function parseIso(iso: string | null): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
}
